import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from 'react-google-recaptcha';
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import PageSEO from "../components/seo/PageSEO";
import { useScrollBehavior } from './useScrollBehavior.js';
import "../styles/home.css";
import "../styles/ethics.css";

const SUBMIT_EMAIL  = "YOUR_AXIS_EMAIL@domain.com";
const RECAPTCHA_KEY = "6LdwstIpAAAAAL1_lBneLQEQRHpyUpxdO5ZXJ0R9";

const QUESTIONS = [
  {
    id: "q1",
    text: "You are part of a response team. Using force could protect several people but risks injury to a bystander and significant property damage. What governs your decision?",
    options: [
      { id: "a", text: "Protection of the group justifies any means necessary." },
      { id: "b", text: "Evaluate all options. Force is the last resort and harm to the bystander is weighed equally with the threat." },
      { id: "c", text: "Do nothing — any action that risks harm to anyone is unacceptable." },
      { id: "d", text: "Act immediately. Hesitation costs lives." },
    ]
  },
  {
    id: "q2",
    text: "A fellow member makes a decision that conflicts with the Sacred Three but claims it was necessary for the mission. What do you do?",
    options: [
      { id: "a", text: "Support them publicly and address it privately later." },
      { id: "b", text: "Say nothing — loyalty to your team comes first." },
      { id: "c", text: "Raise it immediately through the appropriate accountability structure." },
      { id: "d", text: "Report it to outside authorities without addressing it internally first." },
    ]
  },
  {
    id: "q3",
    text: "During planning you discover that your team's intended approach would cause lasting damage to a protected natural area. The objective is legitimate. What is your position?",
    options: [
      { id: "a", text: "The mission takes priority — environmental concerns are secondary to the objective." },
      { id: "b", text: "Raise the concern and propose alternatives that achieve the objective with less environmental impact." },
      { id: "c", text: "Refuse to participate entirely." },
      { id: "d", text: "Complete the mission and document the damage for later review." },
    ]
  },
  {
    id: "q4",
    text: "You are in a position of authority and a member makes an ethical error. How do you handle it?",
    options: [
      { id: "a", text: "Remove them from operations immediately." },
      { id: "b", text: "Ignore it — everyone makes mistakes and it wasn't serious." },
      { id: "c", text: "Address it directly and honestly, use it as a development moment, and hold them accountable to a clear standard going forward." },
      { id: "d", text: "Report it upward and let leadership handle it without your involvement." },
    ]
  },
  {
    id: "q5",
    text: "A community member outside the organization is being exploited and asks for help. Assisting them fully would divert significant resources from your current mission. What do you do?",
    options: [
      { id: "a", text: "Decline — mission obligations come first." },
      { id: "b", text: "Help immediately and completely regardless of the cost to the mission." },
      { id: "c", text: "Assess the situation, determine what level of assistance is proportionate, and act accordingly." },
      { id: "d", text: "Refer them to other resources and continue the mission unchanged." },
    ]
  },
  {
    id: "q6",
    text: "You witness a member using excessive force in a situation where lesser force would have been sufficient. What is your immediate response?",
    options: [
      { id: "a", text: "Do nothing — the situation was handled and the outcome was acceptable." },
      { id: "b", text: "Intervene if it is still ongoing, then address it through the accountability structure regardless of outcome." },
      { id: "c", text: "Mention it privately to the member but let it go." },
      { id: "d", text: "Support the member publicly to maintain team cohesion." },
    ]
  },
  {
    id: "q7",
    text: "In a high-stress scenario your emotional response is pushing you toward an action you know is disproportionate. What governs your decision?",
    options: [
      { id: "a", text: "Your instinct — it developed for a reason and should be trusted." },
      { id: "b", text: "The mission objective, regardless of personal feelings." },
      { id: "c", text: "The Sacred Three. Your feelings are real. They are not the standard." },
      { id: "d", text: "Whatever your team leader decides in the moment." },
    ]
  },
  {
    id: "q8",
    text: "An authority figure within the organization gives you a direct order that you believe violates the Axis code of conduct. What do you do?",
    options: [
      { id: "a", text: "Follow the order — hierarchy exists for a reason and you may be wrong." },
      { id: "b", text: "Refuse and explain why, accepting the consequences of that refusal." },
      { id: "c", text: "Follow the order but document your objection privately." },
      { id: "d", text: "Follow the order this time and raise it as a policy matter afterward." },
    ]
  },
  {
    id: "q9",
    text: "A member you personally trust and value is causing harm to others within the community. How does that relationship affect your response?",
    options: [
      { id: "a", text: "It doesn't — accountability applies equally regardless of personal relationship." },
      { id: "b", text: "You address it more gently because of the trust between you." },
      { id: "c", text: "You ask someone else to handle it to avoid the personal conflict." },
      { id: "d", text: "You give them more time to correct themselves before acting." },
    ]
  },
  {
    id: "q10",
    text: "After a completed operation, it becomes clear that your team made an error that caused unintended harm. No one outside the team is aware of it. What is the correct response?",
    options: [
      { id: "a", text: "Keep it within the team — accountability is an internal matter." },
      { id: "b", text: "Acknowledge it, make it right where possible, and record it honestly in the mission log." },
      { id: "c", text: "Move on — unintended harm is not a moral failing and revisiting it serves no purpose." },
      { id: "d", text: "Disclose it only if directly asked." },
    ]
  },
  {
    id: "q11",
    text: "You are asked to recommend a candidate for advancement. They are highly capable but do not appear genuinely committed to the Sacred Three — their motivation appears to be the skills and the standing alone. What do you do?",
    options: [
      { id: "a", text: "Recommend them — capability is the primary requirement." },
      { id: "b", text: "Decline to recommend them — ethical alignment is not optional at any tier." },
      { id: "c", text: "Recommend them provisionally and hope the culture brings them into alignment." },
      { id: "d", text: "Pass the decision to leadership without sharing your observation." },
    ]
  },
  {
    id: "q12",
    text: "The Axis prefers the least harm in all things. What does that mean when the least harm path is not sufficient to protect what is sacred?",
    options: [
      { id: "a", text: "The preference for least harm is absolute — no exception justifies crossing it." },
      { id: "b", text: "Protection of what is sacred takes precedence. The least harm principle governs method, not whether to act." },
      { id: "c", text: "It means deferring action until a path with no harm is available." },
      { id: "d", text: "It depends entirely on the situation and there is no general principle that applies." },
    ]
  },
  {
    id: "q13",
    text: "You are leading a mission and receive information mid-operation that changes the ethical calculus significantly. The original plan is no longer clearly aligned with the Sacred Three. What do you do?",
    options: [
      { id: "a", text: "Complete the mission — mid-operation changes in plan create greater risk than continuing." },
      { id: "b", text: "Pause, reassess, and make the call based on the new information — even if it means aborting." },
      { id: "c", text: "Complete the current phase and raise the concern in the debrief." },
      { id: "d", text: "Defer to the highest-ranking member present." },
    ]
  },
  {
    id: "q14",
    text: "A long-term member whose judgment you respect argues that a particular action is justified even though it clearly violates one of the Sacred Three. Their reasoning is sophisticated and they make a strong case. How do you respond?",
    options: [
      { id: "a", text: "Reconsider your position — sophisticated reasoning from a trusted source deserves serious weight." },
      { id: "b", text: "The Sacred Three are not negotiable regardless of how strong the argument is. A compelling case for violating them is itself a warning sign." },
      { id: "c", text: "Agree to disagree and abstain from the specific action." },
      { id: "d", text: "Raise it with leadership and let them adjudicate." },
    ]
  },
  {
    id: "q15",
    text: "What is your honest assessment of why you are seeking this level of commitment — and what you are prepared to give up for it?",
    options: [
      { id: "a", text: "I want the skills and the standing. I am willing to accept the obligations that come with them." },
      { id: "b", text: "I believe the Sacred Three are under serious threat and I am prepared to make this the organizing commitment of my life." },
      { id: "c", text: "I have reached the limit of what the previous tier offers and I am ready to go further." },
      { id: "d", text: "I trust the people who recommended this level and I am willing to find out what it requires." },
    ]
  },
];

export default function Tier1Assessment() {
  useScrollBehavior();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [startTime]  = useState(Date.now());

  const [info, setInfo]                     = useState({ firstName: '', lastName: '', email: '' });
  const [answers, setAnswers]               = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [error, setError]                   = useState('');
  const [showSuccess, setShowSuccess]       = useState(false);

  function handleInfoChange(e) {
    const { name, value } = e.target;
    setInfo(prev => ({ ...prev, [name]: value }));
  }

  function handleAnswer(questionId, optionId) {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  }

  const answered    = Object.keys(answers).length;
  const total       = QUESTIONS.length;
  const allAnswered = answered === total;
  const progress    = Math.round((answered / total) * 100);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!info.firstName || !info.lastName || !info.email) {
      setError('Please complete your name and email before submitting.');
      return;
    }
    if (!allAnswered) {
      setError('Please answer all questions before submitting.');
      return;
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }
    if (Date.now() - startTime < 30000) {
      setError('Please take your time with the assessment.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const answerSummary = QUESTIONS.map((q, idx) => {
        const selected = q.options.find(o => o.id === answers[q.id]);
        return `Q${idx + 1}: ${q.text}\nAnswer: ${selected?.text || 'No answer'}`;
      }).join('\n\n');

      const submission = new FormData();
      submission.append('firstName', info.firstName);
      submission.append('lastName',  info.lastName);
      submission.append('email',     info.email);
      submission.append('answers',   answerSummary);
      submission.append('_subject',  `Axis Tier 1 Assessment — ${info.firstName} ${info.lastName}`);
      submission.append('_template', 'table');
      submission.append('_captcha',  'false');
      submission.append('_replyto',  info.email);
      submission.append('_honey',    '');

      const response = await fetch(
        `https://formsubmit.co/ajax/${SUBMIT_EMAIL}`,
        { method: 'POST', headers: { Accept: 'application/json' }, body: submission }
      );
      const result = await response.json();

      if (result.success === 'true' || response.ok) {
        setShowSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="home">
      <PageSEO
        title="Tier 1 Assessment | The Axis"
        description="Axis Tier 1 assessment."
        path="/tier-1-assessment"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebPage", "name": "Axis Tier 1 Assessment" }}
      />
      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">
        <div className="module et__module">

          <div className="et__header">
            <span className="about__eyebrow">Axis — Tier 1</span>
            <h1 className="home__title">Tier 1 Assessment</h1>
            <p className="et__intro">
              This assessment presents complex ethical scenarios and evaluates
              decision-making under pressure, group accountability, and alignment
              with the Sacred Three in situations where that alignment is genuinely
              difficult to maintain.
            </p>
            <p className="et__intro">
              There are no easy answers here by design. Answer based on how you
              would actually act — not on what sounds correct.
            </p>
          </div>

          <form className="et__form" onSubmit={handleSubmit} noValidate>

            <div className="et__section">
              <h2 className="et__section-heading">Your Details</h2>
              <div className="et__info-grid">
                <div className="et__field">
                  <label className="et__label" htmlFor="et-first">First Name</label>
                  <input id="et-first" className="et__input" type="text" name="firstName"
                    value={info.firstName} onChange={handleInfoChange} autoComplete="given-name" required />
                </div>
                <div className="et__field">
                  <label className="et__label" htmlFor="et-last">Last Name</label>
                  <input id="et-last" className="et__input" type="text" name="lastName"
                    value={info.lastName} onChange={handleInfoChange} autoComplete="family-name" required />
                </div>
                <div className="et__field et__field--full">
                  <label className="et__label" htmlFor="et-email">Email</label>
                  <input id="et-email" className="et__input" type="email" name="email"
                    value={info.email} onChange={handleInfoChange} autoComplete="email" required />
                </div>
              </div>
            </div>

            <div className="et__progress-wrap">
              <div className="et__progress-bar">
                <div className="et__progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="et__progress-label">{answered} of {total} answered</span>
            </div>

            <div className="et__questions">
              {QUESTIONS.map((q, idx) => (
                <div key={q.id} className={`et__question ${answers[q.id] ? 'et__question--answered' : ''}`} id={q.id}>
                  <div className="et__question-header">
                    <span className="et__question-number">{String(idx + 1).padStart(2, '0')}</span>
                    <p className="et__question-text">{q.text}</p>
                  </div>
                  <div className="et__options">
                    {q.options.map(opt => (
                      <label key={opt.id} className={`et__option ${answers[q.id] === opt.id ? 'et__option--selected' : ''}`}>
                        <input type="radio" name={q.id} value={opt.id}
                          checked={answers[q.id] === opt.id}
                          onChange={() => handleAnswer(q.id, opt.id)}
                          className="et__radio" />
                        <span className="et__option-marker">{opt.id.toUpperCase()}</span>
                        <span className="et__option-text">{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="et__submit-section">
              {error && <p className="et__error">{error}</p>}
              <div className="et__captcha">
                <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_KEY}
                  onChange={token => setRecaptchaToken(token || '')} theme="dark" />
              </div>
              <button className="btn btn--primary et__submit-btn" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting…" : "Submit Assessment"}
              </button>
              <p className="et__fine-print">
                Your responses are submitted directly to Axis leadership for review.
                You will be contacted at the email address provided.
              </p>
            </div>

          </form>
        </div>
      </section>

      {showSuccess && (
        <div className="et__success-overlay">
          <div className="et__success-box">
            <div className="et__success-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2"/>
                <path d="M15 24l7 7 11-12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="jm__eyebrow">Assessment Received</span>
            <h2 className="et__success-title">Submitted</h2>
            <p className="et__success-body">
              Your assessment has been received. Axis leadership will review
              your responses and be in touch at the email address provided.
            </p>
            <p className="et__success-mantra">
              Reverence is the why. Power is the can. Duty is the must.
            </p>
            <button className="btn btn--primary" onClick={() => navigate('/')}>
              Return Home
            </button>
          </div>
        </div>
      )}
    </main>
  );
}